import React from "react";
import { Phone as PhoneIcon } from "lucide-react";
import { BannerItem } from "@/types/banner";
import { Link } from "@/types/footer";
import { useBannerRotation } from "./useBannerRotation";
import { LinkWrapper } from "@/components/LinkWrapper";
import { socialIcons, SocialPlatform } from "@/lib/socialIcons";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/components/analytics";

type TopUtilityBannerProps = {
  phone: string | null;
  socialLinks: Link[];
  billingUrl: string | null;
  patientPortalUrl: string | null;
  bannerItems: BannerItem[];
  rotationInterval?: number;
};

export function TopUtilityBanner({
  phone,
  socialLinks,
  billingUrl,
  patientPortalUrl,
  bannerItems,
  rotationInterval = 5000,
}: TopUtilityBannerProps) {
  const { currentIndex, isTransitioning, pause, resume } = useBannerRotation({
    itemCount: bannerItems.length,
    interval: rotationInterval,
  });

  const hasAnnouncements = bannerItems.length > 0;
  const currentItem = hasAnnouncements ? bannerItems[currentIndex] : null;
  const hasMultipleItems = bannerItems.length > 1;
  const hasUtilityLinks = billingUrl || patientPortalUrl;

  return (
    <div
      role="region"
      aria-label="Utility navigation"
      className="w-full bg-primary px-6 py-2.5"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <div className="container mx-auto flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex w-full shrink-0 items-center justify-center gap-4 sm:w-auto sm:justify-start">
          {phone && (
            <a
              href={`tel:${phone}`}
              className="flex items-center gap-1.5 text-sm text-primary-foreground transition-opacity hover:opacity-85"
              onClick={() => trackEvent("phone_click", { phone, location: "banner" })}
            >
              <PhoneIcon className="h-4 w-4" aria-hidden="true" />
              <span>{phone}</span>
            </a>
          )}
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-2">
              {socialLinks.map(social => {
                const IconComponent = socialIcons[social.label as SocialPlatform];
                if (!IconComponent) return null;
                return (
                  <a
                    key={social.label}
                    href={social.url}
                    className="text-primary-foreground transition-opacity hover:opacity-85"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    onClick={() => trackEvent("social_click", { platform: social.label, location: "banner" })}
                  >
                    <IconComponent className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {hasAnnouncements && currentItem && (
          <div
            aria-live="polite"
            aria-atomic="true"
            className={cn(
              "order-first flex w-full flex-wrap items-center justify-center gap-1.5 text-center transition-opacity duration-300 ease-in-out lg:order-none lg:w-auto lg:min-w-0 lg:flex-1 lg:gap-2 lg:px-4",
              isTransitioning ? "opacity-0" : "opacity-100"
            )}
          >
            <span className="text-xs font-medium leading-tight text-primary-foreground sm:text-sm">
              {currentItem.text}
              {hasMultipleItems && (
                <span className="sr-only">
                  {` (Announcement ${currentIndex + 1} of ${bannerItems.length})`}
                </span>
              )}
            </span>
            {currentItem.linkText && currentItem.linkUrl && (
              <LinkWrapper
                to={currentItem.linkUrl}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary-foreground/10 px-2.5 py-0.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 focus:ring-offset-2 focus:ring-offset-primary sm:px-3 sm:text-sm"
              >
                {currentItem.linkText}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 sm:h-3.5 sm:w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </LinkWrapper>
            )}
          </div>
        )}

        {hasUtilityLinks && (
          <div className="flex w-full shrink-0 items-center justify-center gap-1 sm:w-auto sm:justify-end">
            {billingUrl && (
              <a
                href={billingUrl}
                className="px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary-foreground transition-colors hover:bg-white/15"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("bill_pay_click")}
              >
                Bill Pay
              </a>
            )}
            {billingUrl && patientPortalUrl && (
              <span className="text-primary-foreground/50">|</span>
            )}
            {patientPortalUrl && (
              <a
                href={patientPortalUrl}
                className="px-3 py-1 text-xs font-medium uppercase tracking-wide text-primary-foreground transition-colors hover:bg-white/15"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("patient_portal_click")}
              >
                Patient Portal
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
